import type Hls from 'hls.js';
import { memo, useCallback, useEffect, useRef } from 'react';

import { ClipType } from '@air/api/types';
import { withByosImgixServerSign } from '@air/component-imgix-image';
import { usePlayerStore } from '@air/component-player';
import { reportErrorToBugsnag } from '@air/utils-error';
import { loadHls } from '@air/utils-video';

import { type CentralizedClip } from '~/store/centralizedClip/types';

export type NativeMobileVideoVisualizerProps = {
  clip: CentralizedClip;
};

export const NativeMobileVideoVisualizer = memo(({ clip }: NativeMobileVideoVisualizerProps) => {
  const hlsRef = useRef<Hls | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const setHlsInstance = usePlayerStore((state) => state.setHlsInstance);
  const setVideoElement = usePlayerStore((state) => state.setVideoElement);

  const handleVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      videoRef.current = element;
      setVideoElement(element);
    },
    [setVideoElement],
  );

  useEffect(() => {
    if (clip.type !== ClipType.video) {
      return;
    }

    let isCancelled = false;

    const loadVideo = async () => {
      try {
        hlsRef.current?.destroy();
        hlsRef.current = null;

        const hlsInstance = await loadHls({
          src: clip.assets.video,
          videoElement: videoRef.current,
        });

        if (isCancelled) {
          hlsInstance?.destroy();
          return;
        }

        hlsRef.current = hlsInstance;
        setHlsInstance(hlsInstance);
      } catch (error) {
        if (isCancelled) {
          return;
        }

        reportErrorToBugsnag({
          context: 'Failed to load HLS video in native mobile visualizer',
          error,
          metadata: {
            data: { src: clip.assets.video },
            key: 'Data',
          },
        });
      }
    };

    void loadVideo();

    return () => {
      isCancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
      setHlsInstance(null);
    };
  }, [clip.assets.video, clip.type, setHlsInstance]);

  // biome-ignore lint/plugin: pre-existing className const; not inlining as a drive-by reformat
  const mediaClassName = 'size-full object-contain';

  if (clip.type === ClipType.audio) {
    return (
      <div className="flex size-full items-center justify-center bg-black p-4">
        {/* Audio must keep its intrinsic control-bar height. `size-full` (height:100%) forces the
            element far taller than the native controls, which makes Android Chrome render a blank box
            with no play button, so audio gets width-only sizing. */}
        {/* biome-ignore lint/a11y/useMediaCaption: True mobile intentionally uses the browser-native media controls. */}
        <audio className="w-full" controls playsInline preload="auto" src={clip.assets.original} />
      </div>
    );
  }

  return (
    <div className="flex size-full items-center justify-center bg-black">
      {/* biome-ignore lint/a11y/useMediaCaption: True mobile intentionally uses the browser-native media controls. */}
      <video
        className={mediaClassName}
        controls
        playsInline
        poster={clip.assets.image ? withByosImgixServerSign(clip.assets.image) : undefined}
        preload="auto"
        ref={handleVideoRef}
      />
    </div>
  );
});

NativeMobileVideoVisualizer.displayName = 'NativeMobileVideoVisualizer';
